<script setup lang="ts">
// Stacked-area sibling of RiskLineChart (2026-10-31, edge-source history spec §7). Bands stack in array order on
// two sides (stackedBands.ts): positive parts above zero, negative parts below, each side drawn outermost-first as
// opaque BaselineSeries anchored at 0 — so every band shows a slice equal to its own value even when some bands are
// negative. Lines (payoff, buy-and-hold) sit on top, unstacked. The crosshair reads each band's own value.
// Same chart pattern as RiskLineChart: whole history loaded, opens on the last `defaultWindow` points, drag
// scrolls, wheel zooms, double-click resets; markers sit on the first series added.
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import {
  createChart, createSeriesMarkers, LineSeries, BaselineSeries, CrosshairMode, LineStyle,
  type IChartApi, type ISeriesApi, type SeriesType, type ISeriesMarkersPluginApi, type SeriesMarker, type Time,
} from 'lightweight-charts'
import { defaultLogicalRange, formatRiskValue, type RiskPoint, type RiskValueFormat } from './riskChartSeries'
import type { RiskChartSeries, RiskChartMarker } from './RiskLineChart.vue'
import { stackBands } from './stackedBands'

export interface RiskChartBand { id: string; label: string; color: string; data: RiskPoint[] }

const props = withDefaults(defineProps<{
  bands: RiskChartBand[]
  lines: RiskChartSeries[]
  markers?: RiskChartMarker[]
  format?: RiskValueFormat
  defaultWindow?: number
  height?: number
}>(), { format: 'number2', defaultWindow: 500, height: 240 })

const BG = '#1a1a1a'
const chartEl = ref<HTMLDivElement | null>(null)
let chart: IChartApi | null = null
const apis: ISeriesApi<SeriesType>[] = []
const order: { kind: 'pos' | 'neg' | 'line'; index: number }[] = []
let markersApi: ISeriesMarkersPluginApi<Time> | null = null
let resizeObserver: ResizeObserver | null = null

const tooltip = ref<{ x: number; y: number; time: string; rows: { label: string; color: string; text: string }[] } | null>(null)
const valueAt = (data: RiskPoint[], time: string) => data.find(p => p.time === time)?.value

const fmt = (v: number) => formatRiskValue(v, props.format)

const stacked = computed(() => stackBands(props.bands))

const legend = computed(() => [
  ...props.bands.map(b => ({ id: b.id, label: b.label, color: b.color, last: b.data.length ? fmt(b.data[b.data.length - 1].value) : '—', dashed: false })),
  ...props.lines.map(l => ({ id: l.id, label: l.label, color: l.color, last: l.data.length ? fmt(l.data[l.data.length - 1].value) : '—', dashed: !!l.dashed })),
])

/** Opaque mix of a colour into the chart background, so stacked fills never blend into each other. */
function mix(hex: string, amount: number): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex)
  const bg = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(BG)!
  if (!m) return hex
  const ch = (i: number) => Math.round(parseInt(m[i], 16) * amount + parseInt(bg[i], 16) * (1 - amount))
  return `rgb(${ch(1)},${ch(2)},${ch(3)})`
}

type AutoscaleInfo = { priceRange: { minValue: number; maxValue: number } } | null
/** Keep zero on the value axis: a stacked band's height only reads as its share when the axis starts at 0. */
function includeZero(original: () => AutoscaleInfo): AutoscaleInfo {
  const r = original()
  if (!r) return r
  return { ...r, priceRange: { minValue: Math.min(0, r.priceRange.minValue), maxValue: Math.max(0, r.priceRange.maxValue) } }
}

function longest(): number {
  return Math.max(0, ...props.bands.map(b => b.data.length), ...props.lines.map(l => l.data.length))
}

function resetWindow() {
  const range = defaultLogicalRange(longest(), props.defaultWindow)
  if (chart && range) chart.timeScale().setVisibleLogicalRange(range)
}

function build() {
  if (!chart) return
  apis.length = 0
  order.length = 0
  for (const side of ['pos', 'neg'] as const) {
    for (let k = props.bands.length - 1; k >= 0; k--) {
      const b = props.bands[k]
      const fill = mix(b.color, 0.55)
      const api = chart.addSeries(BaselineSeries, {
        // one title per band (on its positive side) so the price scale does not repeat each label
        title: side === 'pos' ? b.label : '', lineWidth: 1, lastValueVisible: false, priceLineVisible: false,
        baseValue: { type: 'price', price: 0 },
        autoscaleInfoProvider: includeZero,
        topLineColor: b.color, topFillColor1: fill, topFillColor2: fill,
        bottomLineColor: b.color, bottomFillColor1: fill, bottomFillColor2: fill,
      })
      apis.push(api as ISeriesApi<SeriesType>)
      order.push({ kind: side, index: k })
    }
  }
  props.lines.forEach((l, i) => {
    const api = chart!.addSeries(LineSeries, {
      title: l.label, color: l.color, lineWidth: l.lineWidth ?? 2,
      lineStyle: l.dashed ? LineStyle.Dashed : LineStyle.Solid, lastValueVisible: true, priceLineVisible: false,
    })
    apis.push(api as ISeriesApi<SeriesType>)
    order.push({ kind: 'line', index: i })
  })
  markersApi = null
  setData()
  resetWindow()
}

function applyMarkers() {
  const first = apis[0]
  const wanted = props.markers ?? []
  if (!first || (!wanted.length && !markersApi)) return
  const o = order[0]
  const firstData = !o ? [] : o.kind === 'line' ? props.lines[o.index]?.data : stacked.value[o.kind][o.index]
  const times = (firstData ?? []).map(p => p.time)
  const out: SeriesMarker<Time>[] = []
  for (const m of wanted) {
    const t = times.find(x => x >= m.time)
    if (!t) continue
    out.push({ time: t as Time, position: 'aboveBar', shape: 'circle', text: m.text, color: m.color ?? (m.muted ? '#777777' : '#ffb74d') })
  }
  out.sort((a, b) => String(a.time).localeCompare(String(b.time)))
  if (markersApi) markersApi.setMarkers(out)
  else if (out.length) markersApi = createSeriesMarkers(first as ISeriesApi<SeriesType, Time>, out)
}

function setData() {
  order.forEach((o, i) => apis[i]?.setData(o.kind === 'line' ? props.lines[o.index].data : stacked.value[o.kind][o.index]))
  applyMarkers()
}

onMounted(() => {
  if (!chartEl.value) return
  chart = createChart(chartEl.value, {
    width: chartEl.value.clientWidth || 600,
    height: props.height,
    layout: { background: { color: BG }, textColor: '#888', fontFamily: 'JetBrains Mono, monospace', fontSize: 10 },
    grid: { vertLines: { color: '#262626' }, horzLines: { color: '#262626' } },
    rightPriceScale: { borderColor: '#333', scaleMargins: { top: 0.1, bottom: 0.1 } },
    timeScale: { borderColor: '#333', timeVisible: false, secondsVisible: false, rightOffset: 2 },
    crosshair: { mode: CrosshairMode.Normal },
    localization: { priceFormatter: fmt },
  })
  build()

  // Read-out: each band's OWN value at the hovered date (not the stacked edge), then the lines.
  chart.subscribeCrosshairMove(param => {
    if (!param.time || !param.point) { tooltip.value = null; return }
    const t = String(param.time)
    const rows = [...props.bands, ...props.lines].flatMap(s => {
      const v = valueAt(s.data, t)
      return v == null ? [] : [{ label: s.label, color: s.color, text: fmt(v) }]
    })
    tooltip.value = rows.length ? { x: param.point.x, y: param.point.y, time: t, rows } : null
  })
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (chart && chartEl.value && chartEl.value.clientWidth > 0) chart.applyOptions({ width: chartEl.value.clientWidth })
    })
    resizeObserver.observe(chartEl.value)
  }
})

watch(() => [props.bands, props.lines], () => {
  if (!chart) return
  if (apis.length !== 2 * props.bands.length + props.lines.length) { apis.forEach(a => chart!.removeSeries(a)); build(); return }
  setData()
}, { deep: true })
watch(() => props.markers, applyMarkers, { deep: true })

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  chart?.remove()
  chart = null
})
</script>

<template>
  <div class="rsc">
    <div class="rsc-host-wrap">
      <div ref="chartEl" class="rsc-host" data-testid="stack-chart-host" title="Drag to scroll, wheel to zoom, double-click to reset"
        @dblclick="resetWindow" />
      <div v-if="tooltip" class="rsc-tooltip" data-testid="stack-chart-tooltip" :style="{ left: tooltip.x + 12 + 'px', top: tooltip.y + 12 + 'px' }">
        <div class="rsc-tt-time">{{ tooltip.time }}</div>
        <div v-for="r in tooltip.rows" :key="r.label"><span class="rsc-swatch" :style="{ background: r.color }"></span>{{ r.label }} {{ r.text }}</div>
      </div>
    </div>
    <div class="rsc-legend">
      <span v-for="l in legend" :key="l.id" class="rsc-legend-item">
        <span class="rsc-swatch" :class="{ dashed: l.dashed }" :style="l.dashed ? { borderColor: l.color } : { background: l.color }"></span>{{ l.label }} <b>{{ l.last }}</b>
      </span>
    </div>
  </div>
</template>

<style scoped>
.rsc { display: flex; flex-direction: column; gap: 0.25rem; }
.rsc-host-wrap { position: relative; }
.rsc-host { width: 100%; }
.rsc-tooltip { position: absolute; pointer-events: none; background: #1c2128; border: 1px solid #333; border-radius: 4px;
  padding: 4px 8px; font-family: 'JetBrains Mono', monospace; font-size: 0.72rem; color: #ccc; z-index: 5; white-space: nowrap; }
.rsc-tt-time { color: #888; margin-bottom: 2px; }
.rsc-legend { display: flex; flex-wrap: wrap; gap: 0.25rem 1rem; font-family: 'JetBrains Mono', monospace; font-size: 0.72rem; color: #888; padding: 0 0.25rem; }
.rsc-legend-item b { color: #ccc; font-weight: 600; margin-left: 4px; }
.rsc-swatch { display: inline-block; width: 10px; height: 3px; margin-right: 6px; vertical-align: middle; border-radius: 1px; }
.rsc-swatch.dashed { background: none; border-top: 1px dashed; height: 0; }
</style>
